<template>
  <div class="message-list-content">
    <div
      class="rounded-lg pa-3 elevation-1 text-body-1 position-relative"
      :class="bubbleClasses"
    >
      <template v-if="isMedia">
        <div
          v-if="!isLoaded && !isFetching"
          class="media-placeholder d-flex flex-column align-center justify-center pa-4 rounded cursor-pointer"
          :class="placeholderClasses"
          @click.stop="fetchMedia"
        >
          <v-icon
            size="40"
            :color="isOutgoing ? 'white' : 'primary'"
            class="mb-2"
          >
            {{ mediaIcon }}
          </v-icon>

          <span class="text-caption text-center font-weight-medium">
            {{ meta.extra.caption || mediaLabel }}
          </span>

          <v-chip
            x-small
            class="mt-2 font-weight-bold pointer-events-none"
            :color="isOutgoing ? 'white' : 'primary'"
            :text-color="isOutgoing ? 'primary' : 'white'"
          >
            <v-icon x-small left>mdi-eye</v-icon>
            {{ $i18n.t("medias.preview") }}
          </v-chip>
        </div>

        <div
          v-else-if="isFetching"
          class="media-loading d-flex flex-column align-center justify-center pa-6"
        >
          <v-progress-circular
            indeterminate
            size="32"
            :color="isOutgoing ? 'white' : 'primary'"
            class="mb-2"
          />
          <span class="text-caption">Chargement du média...</span>
        </div>

        <!-- Display image from server -->
        <div v-else class="media-content">
          <!-- IMAGE -->
          <template v-if="detectedType === 'IMAGE'">
            <v-img
              cover
              height="180"
              width="240"
              class="rounded"
              :src="mediaProperties.url"
            >
              <template #placeholder>
                <v-row class="fill-height ma-0" align="center" justify="center">
                  <v-progress-circular indeterminate color="grey lighten-5" />
                </v-row>
              </template>
            </v-img>
          </template>

          <template v-if="detectedType === 'STICKER'">
            <v-img
              contain
              height="150"
              width="150"
              class="mx-auto"
              :src="mediaProperties.url"
            >
              <template #placeholder>
                <v-row class="fill-height ma-0" align="center" justify="center">
                  <v-progress-circular indeterminate color="grey-lighten-2" />
                </v-row>
              </template>
            </v-img>
          </template>

          <!-- VIDEO -->
          <!-- Display video from server -->
          <template v-else-if="detectedType === 'VIDEO'">
            <video
              controls
              autoplay
              width="240"
              height="180"
              class="rounded"
              :src="mediaProperties.url"
            />
          </template>

          <!-- AUDIO -->
          <template v-else-if="detectedType === 'AUDIO'">
            <div class="audio-message">
              <button
                type="button"
                class="audio-play-button"
                :class="{ 'audio-play-button--out': isOutgoing }"
                @click="toggleAudio"
              >
                <v-icon color="white">
                  {{ isAudioPlaying ? "mdi-pause" : "mdi-play" }}
                </v-icon>
              </button>

              <div class="audio-content">
                <div class="audio-progress-wrapper">
                  <input
                    v-model="audioProgress"
                    type="range"
                    min="0"
                    :max="audioDuration || 0"
                    step="0.1"
                    class="audio-progress"
                    :class="{ 'audio-progress--out': isOutgoing }"
                    @input="seekAudio"
                  />
                </div>

                <div class="audio-footer">
                  <span>
                    {{ formatAudioTime(audioCurrentTime) }}
                  </span>

                  <span>
                    {{ formatAudioTime(audioDuration) }}
                  </span>
                </div>
              </div>

              <audio
                ref="audio"
                preload="metadata"
                :src="mediaProperties.url"
                @loadedmetadata="onAudioLoaded"
                @timeupdate="onAudioTimeUpdate"
                @ended="onAudioEnded"
              />
            </div>
          </template>

          <!-- DOCUMENT -->
          <template v-else>
            <div
              class="d-flex align-center pa-2 rounded file-card"
              :class="fileCardClasses"
            >
              <v-icon
                size="36"
                :color="isOutgoing ? 'white' : 'primary'"
                class="mr-3"
              >
                mdi-file-document-outline
              </v-icon>
              <div class="d-flex flex-column flex-grow-1 text-truncate">
                <span class="text-body-2 font-weight-bold text-truncate">
                  {{ meta.extra.document || "Document" }}
                </span>
                <span
                  v-if="formattedFileSize"
                  class="text-caption opacity-80 mb-1"
                >
                  {{ formattedFileSize }}
                </span>
                <a
                  :href="mediaProperties.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-caption text-decoration-underline"
                  :class="isOutgoing ? 'white--text' : 'primary--text'"
                >
                  {{ $i18n.t("medias.download") }}
                </a>
              </div>
            </div>
          </template>
        </div>
      </template>

      <div v-else :class="`content-${direction}`" v-html="formattedContent" />

      <div
        class="d-flex align-center justify-end text-caption mt-1 gap-1"
        :class="footerClasses"
      >
        <span>{{ formattedTime }}</span>
        <message-list-content-status
          v-if="isOutgoing"
          :loading="loading"
          :error="error"
          :status="status"
        />
      </div>
    </div>
  </div>
</template>

<script>
import MessageListContentStatus from "./message-list-content-status";

export default {
  name: "MessageListContent",
  components: {
    MessageListContentStatus,
  },
  props: {
    meta: {
      type: Object,
      default: () => ({
        type: "TEXT",
        caption: null,
      }),
    },
    content: {
      type: String,
      default: "",
    },
    direction: {
      type: String,
      default: "IN",
      validator: (val) => ["IN", "OUT"].includes(val),
    },
    loading: {
      type: Boolean,
      default: false,
    },
    status: {
      type: String,
      default: null,
    },
    error: {
      type: String,
      default: null,
    },
    createdAt: {
      type: [String, Number, Date],
      default: null,
    },
    conversation: {
      type: Object,
      default: () => ({}),
    },
    isFetching: {
      type: Boolean,
      default: false,
    },
    isLoaded: {
      type: Boolean,
      default: false,
    },
    mediaProperties: {
      type: Object,
      default: () => ({
        url: null,
        mimetype: null,
        size: null,
      }),
    },
  },
  data() {
    return {
      isAudioPlaying: false,
      audioCurrentTime: 0,
      audioDuration: 0,
      audioProgress: 0,
    };
  },
  computed: {
    isOutgoing() {
      return this.direction === "OUT";
    },
    isMedia() {
      return ["IMAGE", "VIDEO", "AUDIO", "DOCUMENT", "STICKER"].includes(
        this.meta.type,
      );
    },
    bubbleClasses() {
      return this.isOutgoing
        ? "blue white--text"
        : "grey lighten-3 black--text";
    },
    placeholderClasses() {
      return this.isOutgoing
        ? "blue darken-1 white--text"
        : "grey lighten-2 black--text";
    },
    fileCardClasses() {
      return this.isOutgoing ? "blue darken-1" : "white";
    },
    footerClasses() {
      return this.isOutgoing
        ? "grey--text text--lighten-3"
        : "grey--text text--darken-1";
    },
    mediaIcon() {
      const icons = {
        IMAGE: "mdi-image-outline",
        VIDEO: "mdi-file-video-outline",
        AUDIO: "mdi-file-music-outline",
        DOCUMENT: "mdi-file-document-outline",
        STICKER: "mdi-sticker-outline",
      };
      return icons[this.meta.type] || "mdi-file-outline";
    },
    mediaLabel() {
      if (!this.$i18n) return "Contenu multimédia";

      const labels = {
        IMAGE: this.$i18n.t("chat.messages.type.image"),
        VIDEO: this.$i18n.t("chat.messages.type.video"),
        AUDIO: this.$i18n.t("chat.messages.type.audio"),
        DOCUMENT: this.$i18n.t("chat.messages.type.file"),
        STICKER: this.$i18n.t("chat.messages.type.sticker"),
      };
      return labels[this.meta.type] || "Contenu multimédia";
    },
    formattedContent() {
      if (!this.content) return "";
      return this.$sanitize
        ? this.$sanitize(this.$autolinker.link(this.content))
        : this.content;
    },
    formattedTime() {
      return this.createdAt ? this.$moment(this.createdAt).format("HH:mm") : "";
    },
    detectedType() {
      const mime = this.mediaProperties?.mimetype || "";
      if (mime.startsWith("image/")) return "IMAGE";
      if (mime.startsWith("video/")) return "VIDEO";
      if (mime.startsWith("audio/")) return "AUDIO";
      if (mime === "image/webp") return "STICKER";
      return this.meta.type || "DOCUMENT";
    },
    formattedFileSize() {
      const bytes = this.mediaProperties?.size;
      if (!bytes || isNaN(bytes)) return "";
      const units = ["B", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(1024));
      return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
    },
  },
  methods: {
    fetchMedia() {
      this.$emit("fetch-media");
    },
    toggleAudio() {
      const audio = this.$refs.audio;

      if (!audio) {
        return;
      }

      if (audio.paused) {
        audio.play();
        this.isAudioPlaying = true;
      } else {
        audio.pause();
        this.isAudioPlaying = false;
      }
    },

    onAudioLoaded() {
      const audio = this.$refs.audio;

      if (!audio) {
        return;
      }

      this.audioDuration = audio.duration;
      this.audioProgress = 0;
    },

    onAudioTimeUpdate() {
      const audio = this.$refs.audio;

      if (!audio) {
        return;
      }

      this.audioCurrentTime = audio.currentTime;
      this.audioProgress = audio.currentTime;
    },

    seekAudio() {
      const audio = this.$refs.audio;

      if (!audio) {
        return;
      }

      audio.currentTime = Number(this.audioProgress);
    },

    onAudioEnded() {
      this.isAudioPlaying = false;
      this.audioCurrentTime = 0;
      this.audioProgress = 0;
    },

    formatAudioTime(seconds) {
      if (!seconds || !isFinite(seconds)) {
        return "0:00";
      }

      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.floor(seconds % 60);

      return `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.message-list-content {
  max-width: 320px;

  .cursor-pointer {
    cursor: pointer;
    transition: opacity 0.2s ease, transform 0.1s ease;

    &:hover {
      opacity: 0.9;
    }

    &:active {
      transform: scale(0.98);
    }
  }

  .media-placeholder {
    border: 1px dashed rgba(255, 255, 255, 0.4);
    min-width: 200px;
  }

  .opacity-80 {
    opacity: 0.8;
  }

  .content-OUT {
    a {
      color: #ffffff;
    }
  }
  .pointer-events-none {
    pointer-events: none;
  }
  .audio-message {
    width: 100%;
    min-width: 240px;
    max-width: 300px;

    display: flex;
    align-items: center;

    padding: 6px 4px;
  }

  .audio-play-button {
    width: 36px;
    height: 36px;
    min-width: 36px;

    padding: 0;
    margin-right: 10px;

    border: 0;
    border-radius: 50%;

    display: flex;
    align-items: center;
    justify-content: center;

    background: #2196f3;

    cursor: pointer;

    transition: transform 0.15s ease;

    &:hover {
      transform: scale(1.05);
    }

    &:active {
      transform: scale(0.95);
    }
  }

  .audio-play-button--out {
    background: rgba(255, 255, 255, 0.25);
  }

  .audio-content {
    flex: 1;
    min-width: 0;
  }

  .audio-progress-wrapper {
    width: 100%;
  }

  .audio-progress {
    width: 100%;
    height: 4px;

    margin: 0;
    padding: 0;

    appearance: none;
    -webkit-appearance: none;

    background: rgba(0, 0, 0, 0.25);

    border-radius: 10px;

    cursor: pointer;
  }

  /* Chrome / Safari */
  .audio-progress::-webkit-slider-thumb {
    appearance: none;
    -webkit-appearance: none;

    width: 12px;
    height: 12px;

    border-radius: 50%;
    border: 0;

    background: #2196f3;

    cursor: pointer;
  }

  /* Firefox */
  .audio-progress::-moz-range-thumb {
    width: 12px;
    height: 12px;

    border-radius: 50%;
    border: 0;

    background: #2196f3;

    cursor: pointer;
  }

  .audio-progress--out::-webkit-slider-thumb {
    background: white;
  }

  .audio-progress--out::-moz-range-thumb {
    background: white;
  }

  .audio-footer {
    display: flex;
    justify-content: space-between;

    margin-top: 3px;

    font-size: 11px;

    opacity: 0.75;
  }

  .audio-message audio {
    display: none;
  }
}
</style>
